import React, { useState, useMemo, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { 
  ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, 
  ChevronsLeft, ChevronsRight, Search, X, Download, Printer, Filter,
  SlidersHorizontal, Eye, EyeOff, GripVertical, RotateCcw, Check
} from 'lucide-react';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';

// Column filter component
function ColumnFilter({ column, columnDef }) {
  const columnFilterValue = column.getFilterValue();
  const { filterVariant, filterOptions } = columnDef.meta ?? {};

  if (filterVariant === 'select' && filterOptions) {
    return (
      <Select
        value={columnFilterValue ?? 'all'}
        onValueChange={(value) => column.setFilterValue(value === 'all' ? undefined : value)}
      >
        <SelectTrigger className="h-8 w-full text-xs">
          <SelectValue placeholder="All" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All</SelectItem>
          {filterOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <Input
      type="text"
      value={columnFilterValue ?? ''}
      onChange={(e) => column.setFilterValue(e.target.value)}
      placeholder="Filter..."
      className="h-8 text-xs"
    />
  );
}

export default function AdvancedDataTable({
  data,
  columns,
  onRowClick,
  searchPlaceholder = 'Search...',
  pageSize = 20,
  enablePrint = true,
  enableExport = true,
  exportFilename = 'export',
  getRowId,
  initialColumnVisibility = {},
  initialColumnOrder = null,
  onColumnVisibilityChange,
  onColumnOrderChange,
  hideSearch = false,
  toolbarLeftContent = null,
}) {
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState(initialColumnVisibility);
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize });
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  
  // Column order state - default to the order columns are defined
  const defaultColumnOrder = useMemo(() => columns.map(c => c.accessorKey || c.id), [columns]);
  const [columnOrder, setColumnOrder] = useState(initialColumnOrder || defaultColumnOrder);
  
  // Drag state for column reordering
  const [draggedColumn, setDraggedColumn] = useState(null);

  // Notify parent when column visibility changes
  const handleColumnVisibilityChange = (updater) => {
    setColumnVisibility(prev => {
      const newValue = typeof updater === 'function' ? updater(prev) : updater;
      if (onColumnVisibilityChange) {
        onColumnVisibilityChange(newValue);
      }
      return newValue;
    });
  };
  
  // Notify parent when column order changes
  const handleColumnOrderChange = useCallback((newOrder) => {
    setColumnOrder(newOrder);
    if (onColumnOrderChange) {
      onColumnOrderChange(newOrder);
    }
  }, [onColumnOrderChange]);

  // Drag and drop handlers for column reordering
  const handleDragStart = (e, columnId) => {
    setDraggedColumn(columnId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    if (draggedColumn && draggedColumn !== columnId) {
      const newOrder = [...columnOrder];
      const dragIndex = newOrder.indexOf(draggedColumn);
      const hoverIndex = newOrder.indexOf(columnId);
      
      if (dragIndex !== -1 && hoverIndex !== -1) {
        newOrder.splice(dragIndex, 1);
        newOrder.splice(hoverIndex, 0, draggedColumn);
        handleColumnOrderChange(newOrder);
      }
    }
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
  };

  // Move column up/down in the settings dialog
  const moveColumn = (columnId, direction) => {
    const newOrder = [...columnOrder];
    const index = newOrder.indexOf(columnId);
    if (index === -1) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newOrder.length) return;
    
    // Swap
    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
    handleColumnOrderChange(newOrder);
  };

  // Reset columns to default
  const resetColumns = () => {
    setColumnVisibility({});
    handleColumnOrderChange(defaultColumnOrder);
    if (onColumnVisibilityChange) {
      onColumnVisibilityChange({});
    }
  };

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
      columnFilters,
      columnVisibility,
      columnOrder,
      pagination,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onColumnOrderChange: handleColumnOrderChange,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: getRowId,
  });

  // Export to CSV
  const handleExportCSV = () => {
    const visibleColumns = table.getVisibleLeafColumns();
    const headers = visibleColumns.map(col => col.columnDef.header || col.id);
    
    const rows = table.getFilteredRowModel().rows.map(row => {
      return visibleColumns.map(col => {
        const value = row.getValue(col.id);
        if (value === null || value === undefined) return '';
        if (Array.isArray(value)) return value.join(', ');
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
      });
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${exportFilename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // Print function
  const handlePrint = () => {
    const visibleColumns = table.getVisibleLeafColumns();
    const headers = visibleColumns.map(col => col.columnDef.header || col.id);
    const rows = table.getFilteredRowModel().rows;

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print - ${exportFilename}</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 12px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f5f5f5; font-weight: bold; }
          tr:nth-child(even) { background-color: #fafafa; }
          h1 { font-size: 18px; margin-bottom: 5px; }
          .meta { color: #666; font-size: 11px; margin-bottom: 10px; }
          @media print { body { -webkit-print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <h1>${exportFilename}</h1>
        <div class="meta">Printed on ${new Date().toLocaleString()} | ${rows.length} records</div>
        <table>
          <thead>
            <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
          </thead>
          <tbody>
            ${rows.map(row => `
              <tr>
                ${visibleColumns.map(col => {
                  let value = row.getValue(col.id);
                  if (value === null || value === undefined) value = '';
                  if (Array.isArray(value)) value = value.join(', ');
                  return `<td>${value}</td>`;
                }).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const filteredRowCount = table.getFilteredRowModel().rows.length;
  const totalRowCount = data.length;

  return (
    <div className="space-y-4">
      {/* Toolbar - All items in single row */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* External toolbar content on the left */}
        {toolbarLeftContent}
        
        {/* Search - inline with other buttons (if no external content) */}
        {!hideSearch && !toolbarLeftContent && (
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder={searchPlaceholder}
              value={globalFilter ?? ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="pl-10 pr-10 h-9"
              data-testid="data-grid-search"
            />
            {globalFilter && (
              <button 
                onClick={() => setGlobalFilter('')} 
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <X className="h-4 w-4 text-slate-400 hover:text-slate-600" />
              </button>
            )}
          </div>
        )}

        {/* Spacer to push buttons to the right */}
        <div className="flex-1" />

        {/* Column Filters Toggle */}
        <Button
          variant={showColumnFilters ? 'default' : 'outline'}
          size="sm"
          onClick={() => setShowColumnFilters(!showColumnFilters)}
          className="gap-2"
          data-testid="toggle-filters"
        >
          <Filter className="h-4 w-4" />
          Filters
        </Button>
        
        {/* Column Settings Button - Opens Dialog */}
        <Button 
          variant="outline" 
          size="sm" 
          className="gap-2" 
          onClick={() => setColumnSettingsOpen(true)}
          data-testid="column-visibility"
        >
          <SlidersHorizontal className="h-4 w-4" />
          Columns
        </Button>

          {/* Export CSV */}
          {enableExport && (
            <Button variant="outline" size="sm" onClick={handleExportCSV} className="gap-2" data-testid="export-csv">
              <Download className="h-4 w-4" />
              Export
            </Button>
          )}

          {/* Print */}
          {enablePrint && (
            <Button variant="outline" size="sm" onClick={handlePrint} className="gap-2" data-testid="print-btn">
              <Printer className="h-4 w-4" />
              Print
            </Button>
          )}
      </div>

      {/* Column Settings Dialog */}
      <Dialog open={columnSettingsOpen} onOpenChange={setColumnSettingsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SlidersHorizontal className="h-5 w-5 text-indigo-600" />
              Customize Columns
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-slate-500 mb-3">
              Drag to reorder columns. Toggle visibility with checkboxes.
            </p>
            <div className="space-y-1 max-h-[400px] overflow-y-auto border rounded-lg p-2">
              {columnOrder.map((columnId, index) => {
                const column = table.getAllLeafColumns().find(c => (c.columnDef.accessorKey || c.id) === columnId);
                if (!column || columnId === 'select' || columnId === 'actions') return null;
                
                const isVisible = column.getIsVisible();
                const headerText = typeof column.columnDef.header === 'string' 
                  ? column.columnDef.header 
                  : columnId;
                
                return (
                  <div
                    key={columnId}
                    draggable
                    onDragStart={(e) => handleDragStart(e, columnId)}
                    onDragOver={(e) => handleDragOver(e, columnId)}
                    onDragEnd={handleDragEnd}
                    className={`flex items-center gap-2 p-2 rounded-md border transition-all ${
                      draggedColumn === columnId 
                        ? 'bg-indigo-50 border-indigo-300 opacity-50' 
                        : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    } ${!isVisible ? 'opacity-60' : ''}`}
                  >
                    <GripVertical className="h-4 w-4 text-slate-400 cursor-grab flex-shrink-0" />
                    <Checkbox
                      checked={isVisible}
                      onCheckedChange={(checked) => column.toggleVisibility(!!checked)}
                      className="flex-shrink-0"
                    />
                    <span className={`flex-1 text-sm ${isVisible ? 'text-slate-700' : 'text-slate-400'}`}>
                      {headerText}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => moveColumn(columnId, 'up')}
                        disabled={index === 0}
                        className="p-1 rounded hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Move up"
                      >
                        <ChevronUp className="h-3 w-3 text-slate-500" />
                      </button>
                      <button
                        onClick={() => moveColumn(columnId, 'down')}
                        disabled={index === columnOrder.length - 1}
                        className="p-1 rounded hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed"
                        title="Move down"
                      >
                        <ChevronDown className="h-3 w-3 text-slate-500" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter className="flex justify-between sm:justify-between">
            <Button variant="outline" size="sm" onClick={resetColumns} className="gap-2">
              <RotateCcw className="h-4 w-4" />
              Reset to Default
            </Button>
            <Button size="sm" onClick={() => setColumnSettingsOpen(false)} className="gap-2">
              <Check className="h-4 w-4" />
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Results count */}
      <div className="text-sm text-slate-500">
        {filteredRowCount === totalRowCount ? (
          <span>{totalRowCount} records</span>
        ) : (
          <span>Showing {filteredRowCount} of {totalRowCount} records</span>
        )}
        {(globalFilter || columnFilters.length > 0) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setGlobalFilter('');
              setColumnFilters([]);
            }}
            className="ml-2 h-6 text-xs"
          >
            Clear filters
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-md border bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <React.Fragment key={headerGroup.id}>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    {headerGroup.headers.map((header) => (
                      <TableHead 
                        key={header.id}
                        className="font-semibold text-slate-700"
                        style={{ width: header.column.columnDef.size }}
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            className={`flex items-center gap-1 ${
                              header.column.getCanSort() ? 'cursor-pointer select-none hover:text-slate-900' : ''
                            }`}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {header.column.getCanSort() && (
                              <span className="ml-1">
                                {{
                                  asc: <ChevronUp className="h-4 w-4" />,
                                  desc: <ChevronDown className="h-4 w-4" />,
                                }[header.column.getIsSorted()] ?? (
                                  <ChevronsUpDown className="h-4 w-4 text-slate-300" />
                                )}
                              </span>
                            )}
                          </div>
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                  
                  {/* Column Filters Row */}
                  {showColumnFilters && (
                    <TableRow className="bg-slate-100/50 hover:bg-slate-100/50">
                      {headerGroup.headers.map((header) => (
                        <TableHead key={`filter-${header.id}`} className="py-2">
                          {header.column.getCanFilter() ? (
                            <ColumnFilter column={header.column} columnDef={header.column.columnDef} />
                          ) : null}
                        </TableHead>
                      ))}
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    className={onRowClick ? 'cursor-pointer' : ''}
                    onClick={() => onRowClick?.(row.original)}
                    data-testid={`row-${row.id}`}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center text-slate-500">
                    No results found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <span>Rows per page:</span>
          <Select
            value={String(pagination.pageSize)}
            onValueChange={(value) => setPagination(prev => ({ ...prev, pageSize: Number(value), pageIndex: 0 }))}
          >
            <SelectTrigger className="h-8 w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 20, 50, 100].map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">
            Page {pagination.pageIndex + 1} of {table.getPageCount() || 1}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
